import React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Form, Field } from 'react-final-form';
import { ToastContainer, notify } from '../../libraries/notifications';
import config from '../../config';
import { calculateBalanceFromTrx, capitalize, validateIsfilled } from '../../libraries/utils';

import LayoutWithSidebar from '../../components/layout/LayoutWithSidebar';
import PanelWindow from '../../components/PanelWindow';
import FormLayout from '../../components/forms/FormLayout';
import TextInput from '../../components/forms/TextInput';
import SelectInput from '../../components/forms/SelectInput';
import IntegerInput from '../../components/forms/IntegerInput';

import transactionsActions from '../../context/transactions/actions';
import usersActions from '../../context/users/actions';
import companiesActions from '../../context/companies/actions';

class CreditTransactionsDebug extends React.Component {
  constructor(props) {
    super(props);
    this.t = this.props.t;
    this.state = {
      loading: true,
      users: [],
      company: null,
      selectedUser: null,
      userBalance: null,
      companyBalance: null,
      transactions: [],
      submitting: false,
    };
    this.breadcrumbs = [capitalize(this.t('Debug')), this.t('Transacciones de Créditos')];
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = async () => {
    this.setState({ loading: true });
    await this.getCompany();
    await this.getUsers();
    await this.getTransactions();
    this.setState({ loading: false });
  };

  getCompany = async () => {
    const ownerParam = this.props.auth.user.accounts
      ? this.props.auth.user.accounts[0].id
      : this.props.auth.user.id;

    await this.props.onGetCompanies({ owner: ownerParam });
    const { companies } = this.props;
    
    if (companies.error) {
      notify(this.t(companies.error.message));
    } else if (companies.items.length > 0) {
      this.setState({ company: companies.items[0] });
    }
  };

  getUsers = async () => {
    await this.props.onGetUsers();
    const { users } = this.props;
    
    if (users.error) {
      notify(this.t(users.error.message));
    } else {
      this.setState({ users: users.items });
    }
  };

  getTransactions = async () => {
    if (!this.state.company) return;

    const params = {
      owner: this.state.company.id,
      type: 'credits',
      status: 'paid',
    };

    await this.props.onGetTransactions(params);
    const { transactions } = this.props;

    if (transactions.error) {
      notify(this.t(transactions.error.message));
    } else {
      this.setState({ transactions: transactions.items });
      this.calculateBalances();
    }
  };

  calculateBalances = () => {
    const { transactions, company, selectedUser } = this.state;

    if (company) {
      const companyBalance = calculateBalanceFromTrx(transactions, company.id);
      this.setState({ companyBalance });
    }

    if (selectedUser) {
      const userBalance = calculateBalanceFromTrx(transactions, selectedUser);
      this.setState({ userBalance });
    }
  };

  onUserChange = (userId) => {
    this.setState({ selectedUser: userId }, () => {
      this.calculateBalances();
    });
  };

  onSubmit = async (values) => {
    this.setState({ submitting: true });

    const data = {
      type: 'credits',
      sub_type: 'credits',
      source: values.source || null,
      target: values.target || null,
      owner: values.owner || this.state.company?.id,
      status: 'paid',
      total: parseInt(values.total) || 0,
      untaxed_amount: parseInt(values.total) || 0,
      currency: 'ARS',
      json_data: {
        reason: values.reason || 'Debug transaction',
        debug: true,
      },
    };

    console.log('Creating transaction:', data);

    await this.props.onSaveTransaction(data);
    const { transaction } = this.props;

    if (transaction.error) {
      notify(this.t(transaction.error.message));
    } else {
      notify('¡Transacción creada exitosamente!', 'SUCCESS');
      // Reload transactions to see updated balances
      await this.getTransactions();
    }

    this.setState({ submitting: false });
  };

  render() {
    const { loading, users, company, selectedUser, userBalance, companyBalance, submitting } = this.state;

    const userOptions = users.map(u => ({
      value: u.id,
      label: `${u.name || u.email} (${u.email})`,
    }));

    // Add special accounts to source/target options
    const accountOptions = [
      { value: '', label: '-- Seleccionar Cuenta --' },
      { value: 'SYSTEM', label: 'SYSTEM (Créditos del sistema)' },
      { value: 'RESERVED', label: 'RESERVED (Créditos reservados)' },
      { value: 'ARCHIVED', label: 'ARCHIVED (Archivados/consumidos)' },
      { value: 'CONSUMED', label: 'CONSUMED (Créditos consumidos)' },
      ...(company ? [{ value: company.id, label: `Company: ${company.name}` }] : []),
      ...users.map(u => ({
        value: u.id,
        label: `User: ${u.name || u.email}`,
      })),
    ];

    const actions = {
      main: {
        onClick: () => this.submit && this.submit(),
        title: this.t('Crear Transacción'),
        icon: 'checkmark',
        disabled: submitting,
      },
      secondaries: [
        {
          onClick: () => this.loadData(),
          title: this.t('Actualizar Datos'),
          icon: 'refresh',
          disabled: submitting,
        },
      ],
    };

    return (
      <LayoutWithSidebar
        main={{ className: 'text-content-400' }}
        header={{ breadcrumbs: this.breadcrumbs }}
        container={{ className: 'px-8' }}
        actions={actions}
      >
        <ToastContainer />
        
        {/* Balance Display Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Company Balance */}
          <div className="card bg-blue-50 p-4">
            <h3 className="text-lg font-bold mb-2">Balance de la Empresa</h3>
            {company && (
              <>
                <p className="text-sm text-gray-600">{company.name}</p>
                <p className="text-sm text-gray-500">ID: {company.id}</p>
                {companyBalance && (
                  <div className="mt-3">
                    <p className="text-2xl font-bold text-blue-700">
                      {companyBalance.balance} créditos
                    </p>
                    <p className="text-sm text-yellow-600">
                      Reservados: {companyBalance.reservedBalance}
                    </p>
                    <p className="text-sm text-green-600">
                      Disponibles: {companyBalance.balance - companyBalance.reservedBalance}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* User Balance */}
          <div className="card bg-green-50 p-4">
            <h3 className="text-lg font-bold mb-2">Balance del Usuario Seleccionado</h3>
            {selectedUser ? (
              <>
                <p className="text-sm text-gray-600">
                  {users.find(u => u.id === selectedUser)?.name || users.find(u => u.id === selectedUser)?.email}
                </p>
                <p className="text-sm text-gray-500">ID: {selectedUser}</p>
                {userBalance && (
                  <div className="mt-3">
                    <p className="text-2xl font-bold text-green-700">
                      {userBalance.balance} créditos
                    </p>
                    <p className="text-sm text-yellow-600">
                      Reservados: {userBalance.reservedBalance}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-gray-400">Selecciona un usuario para ver su balance</p>
            )}
          </div>

          {/* Transaction Info */}
          <div className="card bg-purple-50 p-4">
            <h3 className="text-lg font-bold mb-2">Reglas de Transacciones</h3>
            <div className="text-sm space-y-1">
              <p><strong>source:</strong> Quién pierde créditos</p>
              <p><strong>target:</strong> Quién gana créditos</p>
              <p><strong>owner:</strong> Contexto de empresa</p>
              <hr className="my-2" />
              <p className="text-xs text-gray-600">
                • Si source = ID usuario → Usuario es debitado<br />
                • Si target = ID usuario → Usuario es acreditado<br />
                • Usa SYSTEM, RESERVED, CONSUMED para cuentas especiales
              </p>
            </div>
          </div>
        </div>

        <PanelWindow outerTitle="Crear Transacción de Créditos" loading={loading}>
          <Form
            onSubmit={this.onSubmit}
            initialValues={{
              source: '',
              target: '',
              owner: company?.id || '',
              total: 0,
              reason: '',
            }}
          >
            {({ handleSubmit, form, values }) => {
              this.submit = handleSubmit;
              return (
                <FormLayout form={form} onSubmit={this.onSubmit} values={values}>
                  <div className="mb-6 p-4 bg-gray-100 rounded">
                    <h4 className="font-bold mb-3">Seleccionar Usuario para Ver Balance</h4>
                    <select
                      className="select select-bordered w-full"
                      onChange={(e) => this.onUserChange(e.target.value)}
                      defaultValue=""
                    >
                      <option value="" disabled>-- Selecciona un usuario --</option>
                      {userOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Source */}
                    <Field
                      name="source"
                      component={SelectInput}
                      options={accountOptions}
                      label="Origen (quién pierde créditos)"
                      placeholder="Select source"
                      className="w-full"
                    />

                    {/* Target */}
                    <Field
                      name="target"
                      component={SelectInput}
                      options={accountOptions}
                      label="Destino (quién gana créditos)"
                      placeholder="Select target"
                      className="w-full"
                    />

                    {/* Owner */}
                    <Field
                      name="owner"
                      component={TextInput}
                      label="Propietario (ID de empresa)"
                      placeholder="Company ID"
                      readOnly
                      className="w-full"
                    />

                    {/* Total */}
                    <Field
                      name="total"
                      component={IntegerInput}
                      label="Cantidad de Créditos"
                      placeholder="Amount"
                      className="w-full"
                    />
                  </div>

                  {/* Reason */}
                  <Field
                    name="reason"
                    component={TextInput}
                    label="Razón"
                    placeholder="Transaction reason"
                    className="w-full mt-4"
                  />

                  {/* Preview */}
                  <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded">
                    <h4 className="font-bold mb-2">Vista Previa de la Transacción</h4>
                    <pre className="text-xs overflow-auto">
                      {JSON.stringify(
                        {
                          type: 'credits',
                          source: values.source || 'null',
                          target: values.target || 'null',
                          owner: values.owner || company?.id,
                          total: values.total,
                          reason: values.reason,
                        },
                        null,
                        2
                      )}
                    </pre>
                  </div>
                </FormLayout>
              );
            }}
          </Form>
        </PanelWindow>
      </LayoutWithSidebar>
    );
  }
}

const mapStateToProps = (state) => ({
  auth: state.users.auth,
  users: state.users.list,
  companies: state.companies.list,
  transactions: state.transactions.list,
  transaction: state.transactions.current,
});

const mapDispatchToProps = (dispatch) => ({
  onGetUsers: () => dispatch(usersActions.getAll()),
  onGetCompanies: (params) => dispatch(companiesActions.getAll(params)),
  onGetTransactions: (params) => dispatch(transactionsActions.getAll(params)),
  onSaveTransaction: (data) => dispatch(transactionsActions.saveOrUpdate(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(CreditTransactionsDebug));
